import { Box, Text } from '@chakra-ui/react';
import React from 'react';

import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { FOOTER_H } from '../../styles/tokens';
import { Slot } from '../Slot';

/** Monospace stack for the identifier value, matching `PatientInfo`'s identifier-row values. */
const MONO_STACK =
  '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Page footer's RIGHT side -- the case identifier
 * (`footer.caseIdentifier.contentId`: `'case.identifier'` in coding, `'workitem.identifier'` in
 * grading). Renders a static "CASE" label followed by the bound identifier value in monospace,
 * vertically centered in the footer strip.
 *
 * Both the label and the value are gated on a present value: with no bound identifier (the
 * empty-shell default) the region renders nothing -- the bare `Slot`'s `renderPlainText(undefined)`
 * fallback is `null` -- preserving the empty-by-default contract. A `componentId` on the marker
 * still renders its component in place of the plain value, inside the same "CASE"-labelled frame.
 *
 * DATUM CONTAINER: `caseIdentifier` (a `DataContainerMarker`, schema.ts) carries no `visible` toggle
 * and no item list -- only its own `contentKey` + `contentId` -- so this molecule is always rendered
 * and has no alternate-config case. It is itself the `Slot` item, bound to its own `contentId`; the
 * region `<Box>` carries `data-content-key` only, never a `data-slot-key` (that attribute is for
 * orderable list ITEMS, not a container's own single bound datum).
 */
export const CaseIdentifier: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { caseIdentifier } = config.footer;
  const value = readState(state, caseIdentifier.contentId);
  const hasValue = value != null;

  const slot = (
    <Slot
      item={caseIdentifier}
      value={value}
      mode={mode}
      fallback={() => renderPlainText(value)}
    />
  );

  return (
    <Box
      role="region"
      aria-label="Case identifier"
      data-content-key={caseIdentifier.contentKey}
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap="7px"
      style={{
        height: FOOTER_H,
        minHeight: FOOTER_H
      }}
    >
      {hasValue ? (
        <>
          <Text
            as="span"
            fontSize="9.5px"
            fontWeight={700}
            letterSpacing="0.66px"
            textTransform="uppercase"
            color="#7a7682"
          >
            Case
          </Text>
          <Text
            as="span"
            fontSize="11px"
            fontWeight={600}
            fontFamily={MONO_STACK}
            color="#1a1818"
          >
            {slot}
          </Text>
        </>
      ) : (
        slot
      )}
    </Box>
  );
};
